import { useEffect, useRef, useState } from 'react';
import { Camera, Mic, Volume2, AlertCircle, CheckCircle2, XCircle, Loader2, RefreshCw, Play } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { useMediaDevices } from '../hooks/useMediaDevices';

function PermissionBadge({ state }) {
  if (state === 'granted') return <span className="inline-flex items-center gap-1 text-green-400 text-xs"><CheckCircle2 size={12} /> Autorisé</span>;
  if (state === 'denied') return <span className="inline-flex items-center gap-1 text-red-400 text-xs"><XCircle size={12} /> Bloqué</span>;
  if (state === 'prompt') return <span className="inline-flex items-center gap-1 text-yellow-400 text-xs"><AlertCircle size={12} /> À autoriser</span>;
  return <span className="text-zinc-500 text-xs">Non testé</span>;
}

/**
 * DeviceTester — verifies camera, microphone (with live level meter) and speakers.
 * Reusable: works both on /settings page and inside a modal.
 *
 * Props:
 *   compact?: boolean — tighter layout for modal usage
 */
export default function DeviceTester({ compact = false }) {
  const { devices, permission, selected, select, refresh, setPermission } = useMediaDevices();
  const [streamErr, setStreamErr] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [level, setLevel] = useState(0); // 0..100
  const [playingTone, setPlayingTone] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  const cleanupStream = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => {});
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setLevel(0);
    setStreaming(false);
  };

  const startStream = async () => {
    setStreamErr(null);
    setRequesting(true);
    cleanupStream();
    try {
      const constraints = {
        video: {
          deviceId: selected.camera ? { exact: selected.camera } : undefined,
          width: { ideal: 640 }, height: { ideal: 480 },
        },
        audio: {
          deviceId: selected.mic ? { exact: selected.mic } : undefined,
          echoCancellation: true, noiseSuppression: true,
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setStreaming(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      // Permissions are granted now if we got here
      setPermission({ camera: 'granted', mic: 'granted' });
      await refresh(); // labels become available

      // Audio analyser
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctor();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyserRef.current = analyser;

        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          // RMS computation
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);
          const pct = Math.min(100, Math.round(rms * 250));
          setLevel(pct);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      }
    } catch (err) {
      const name = err?.name || 'Error';
      const msg = name === 'NotAllowedError'
        ? "Permission refusée. Autorise caméra et micro dans le navigateur."
        : name === 'NotFoundError'
          ? 'Aucun périphérique caméra/micro trouvé.'
          : `Erreur: ${err?.message || name}`;
      setStreamErr(msg);
      if (name === 'NotAllowedError') {
        setPermission((p) => ({ ...p, camera: 'denied', mic: 'denied' }));
      }
    } finally {
      setRequesting(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => () => cleanupStream(), []);

  const playTestTone = () => {
    setPlayingTone(true);
    const Ctor = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 440;
    gain.gain.value = 0.15;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    setTimeout(() => {
      osc.stop();
      ctx.close().catch(() => {});
      setPlayingTone(false);
    }, 800);
  };

  return (
    <div className="space-y-5" data-testid="device-tester">
      {/* Camera preview */}
      <div className="glass-light rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Camera size={16} className="text-pink-400" />
            <span className="font-semibold text-white text-sm">Caméra</span>
          </div>
          <PermissionBadge state={permission.camera} />
        </div>
        <div className={`relative ${compact ? 'aspect-[4/3]' : 'aspect-video'} rounded-xl overflow-hidden bg-black/40 border border-white/5 mb-3`}>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            muted
            playsInline
            data-testid="device-camera-preview"
          />
          {!streaming && !requesting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 text-sm gap-2">
              <Camera size={28} />
              <span>Aperçu désactivé</span>
            </div>
          )}
          {requesting && (
            <div className="absolute inset-0 flex items-center justify-center text-pink-400">
              <Loader2 className="animate-spin" />
            </div>
          )}
        </div>
        {devices.cameras.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">Périphérique</Label>
            <select
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl h-10 px-3 text-sm"
              value={selected.camera || ''}
              onChange={(e) => select('camera', e.target.value)}
              data-testid="device-camera-select"
            >
              <option value="">Par défaut</option>
              {devices.cameras.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || `Caméra ${d.deviceId.slice(0, 6)}`}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Mic level meter */}
      <div className="glass-light rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Mic size={16} className="text-purple-400" />
            <span className="font-semibold text-white text-sm">Microphone</span>
          </div>
          <PermissionBadge state={permission.mic} />
        </div>
        <div className="bg-black/40 rounded-xl h-6 overflow-hidden border border-white/5 mb-2 relative" data-testid="device-mic-meter">
          <div
            className="h-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 transition-[width] duration-75"
            style={{ width: `${level}%` }}
          />
          {streaming && level < 3 && (
            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-zinc-400">Parle pour tester...</div>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 mb-2">Niveau d&apos;entrée audio (parle pour voir la barre bouger)</div>
        {devices.mics.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">Périphérique</Label>
            <select
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl h-10 px-3 text-sm"
              value={selected.mic || ''}
              onChange={(e) => select('mic', e.target.value)}
              data-testid="device-mic-select"
            >
              <option value="">Par défaut</option>
              {devices.mics.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || `Micro ${d.deviceId.slice(0, 6)}`}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Speakers test */}
      <div className="glass-light rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Volume2 size={16} className="text-blue-400" />
            <span className="font-semibold text-white text-sm">Haut-parleurs</span>
          </div>
          <Button
            type="button"
            onClick={playTestTone}
            disabled={playingTone}
            className="h-8 px-3 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 border-0 rounded-lg"
            data-testid="device-speaker-test-btn"
          >
            {playingTone ? <><Loader2 size={12} className="animate-spin mr-1" /> Lecture...</> : <><Play size={12} className="mr-1" /> Tester</>}
          </Button>
        </div>
        <p className="text-[11px] text-zinc-500">Un son de 440 Hz pendant 0,8 s. Si tu n&apos;entends rien, vérifie le volume.</p>
      </div>

      {/* Error / Action */}
      {streamErr && (
        <div className="glass-light rounded-2xl p-3 border border-red-500/30 text-sm text-red-300 flex items-start gap-2" data-testid="device-error">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <div>{streamErr}</div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          type="button"
          onClick={startStream}
          disabled={requesting}
          className="flex-1 h-11 btn-neon text-white border-0 rounded-xl font-bold"
          data-testid="device-start-btn"
        >
          {requesting ? <Loader2 size={16} className="animate-spin" /> : streaming ? 'Relancer le test' : 'Démarrer le test'}
        </Button>
        {streaming && (
          <Button
            type="button"
            onClick={() => cleanupStream()}
            variant="outline"
            className="h-11 bg-white/5 text-white border-white/15 rounded-xl"
            data-testid="device-stop-btn"
          >
            <RefreshCw size={14} className="mr-1.5" /> Arrêter
          </Button>
        )}
      </div>
    </div>
  );
}
